import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

// Alerts speak in tonal ink + paper, with the gold-soft border as the only chromatic cue.
// Avoids the green/yellow/red palette in line with the Auvers anti-third-accent rule.
const alertVariants = cva(
  "relative w-full rounded-md border p-4 font-sans text-[14px] leading-[1.55] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg~*]:pl-7",
  {
    variants: {
      variant: {
        default:
          "border-rule bg-paper-2 text-ink [&>svg]:text-ink-soft",
        destructive:
          "border-[#c19388] bg-[#f5e3df] text-[#5b2419] [&>svg]:text-[#7a3325]",
        warning:
          "border-gold-soft bg-[#fbf3dd] text-[#5a4419] [&>svg]:text-gold",
        success:
          "border-rule bg-paper-3 text-ink [&>svg]:text-gold",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, children, ...props }, ref) => (
  <div
    ref={ref}
    role="alert"
    className={cn(alertVariants({ variant }), className)}
    {...props}
  >
    {children}
  </div>
))
Alert.displayName = "Alert"

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn("mb-1 font-sans text-[13px] font-medium uppercase tracking-[0.06em] leading-none", className)}
    {...props}
  />
))
AlertTitle.displayName = "AlertTitle"

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-[14px] [&_p]:leading-[1.55]", className)}
    {...props}
  />
))
AlertDescription.displayName = "AlertDescription"

export { Alert, AlertTitle, AlertDescription, alertVariants }
